import _ from 'lodash';
import React, { ReactNode, useEffect, useRef, useState } from 'react';
import { useApolloClient, useMutation } from '@apollo/client';
import { useSnackbar } from 'notistack';
import * as Sentry from '@sentry/react';

import { createStore } from '../../../lib/stores/create-store';
import { removeTrackedQuery, getTrackedQueries, setTrackedQueries } from '../../../lib/utils/tracker.link';
import { UPDATE_HANDLERS } from '../../../lib/services/update-handler.service';

import LoadingPage from '../../_pages/_boilerplate/LoadingPage/loading-page.container';
import { useQueueOpen, setIsSyncing, setOfflineSyncSettled } from '../../../lib/stores/connectivity.store';
import { RECORD_USER_SESSION } from '../../../lib/queries/user-session.queries';
import { store } from '../../../lib/utils/storage';
import { getAuthUser } from '../../../lib/services/auth.service';
import { buildUserSessionActionsEnvelope, filterUserSessions, getCachedUserSessions, removeUserSessionActions } from '../../../lib/utils/user-session.service';
import { actionKey, ackedActionKeys, refusedScoreActionKeys, refusedScoreReasons } from '../../../lib/helpers/user-action-ack.helpers';
import { useRefusedScoresSnackbar } from '../../../lib/hooks/use-refused-scores-snackbar';
import { useClientUpdateSnackbar } from '../../../lib/hooks/use-client-update-snackbar';
import { decideReplayOutcome, hasGraphQLError } from '../../../lib/helpers/tracked-query.helpers';
import { tryRecordSyncPass, wedgedKeys, pruneSyncAttempts } from '../../../lib/helpers/sync-attempts.helpers';
import { IntlMsg } from '../IntlMsg/intl-msg.component';
import { messages } from './offline-handler.messages';
import { toEpochMillis } from '../../../lib/helpers/date.helpers';
import { Box, CircularProgress, Typography } from '@material-ui/core';
import { ENABLE_SENTRY } from '../../../../_configuration';

interface IOfflineHydrator {
  children: ReactNode;
}

let timeout: any;
export const USER_SESSIONS_LOCAL_REF = 'max_UserSessions';
export const QUERY_LOCAL_REF = 'max_TrackedQueries';

const [getIsSavingScores, getIsSavingScoresValue, setIsSavingScores] = createStore<boolean>(false);
export { getIsSavingScores, getIsSavingScoresValue };

// One score-submit flow at a time: taken only through a claim, given back only through the release.
// See "One score-submit flow at a time" in CLAUDE.md.
const savingScoresWaiters: Array<() => void> = [];

export const claimSavingScores = (): boolean => {
  if (getIsSavingScoresValue()) return false;
  setIsSavingScores(true);
  return true;
};

export const waitToClaimSavingScores = (): Promise<void> =>
  claimSavingScores() ? Promise.resolve() : new Promise((resolve) => { savingScoresWaiters.push(resolve); });

// Hands a held flag straight to the next waiter, so no claim can slip in between.
export const releaseSavingScores = () => {
  const next = savingScoresWaiters.shift();
  if (next) next();
  else setIsSavingScores(false);
};

// Per-consumer refetch-on-sync ledger: a hash keyed by each consumer's own reference,
// holding whether it still owes a refetch for the latest sync. See useRefetchAfterOfflineSync.
const [getOfflineSyncPending, getOfflineSyncPendingValue, setOfflineSyncPending] =
  createStore<Map<object, boolean>>(new Map());
export { getOfflineSyncPending, setOfflineSyncPending };

// Copy-on-write update of the ledger: `shouldUpdate` gates the publish (skip it when
// nothing changes, so no spurious re-render), then `mutate` runs on a fresh clone.
const updateOfflineSyncPending = (
  shouldUpdate: (current: Map<object, boolean>) => boolean,
  mutate: (next: Map<object, boolean>) => void,
) => {
  const current = getOfflineSyncPendingValue();
  if (!shouldUpdate(current)) return;
  const next = new Map(current);
  mutate(next);
  setOfflineSyncPending(next);
};

// A consumer adds/removes its own entry across its lifetime (mount → unmount).
export const registerOfflineSyncConsumer = (key: object) =>
  updateOfflineSyncPending((current) => !current.has(key), (next) => next.set(key, false));
export const unregisterOfflineSyncConsumer = (key: object) =>
  updateOfflineSyncPending((current) => current.has(key), (next) => next.delete(key));

// Called once per completed offline sync: every registered consumer now owes a refetch.
export const markOfflineSyncPending = () =>
  updateOfflineSyncPending((current) => current.size > 0, (next) => next.forEach((_pending, key) => next.set(key, true)));

// A consumer clears its own entry once it has refetched.
export const completeOfflineSyncForConsumer = (key: object) =>
  updateOfflineSyncPending((current) => !!current.get(key), (next) => next.set(key, false));


const actionSort = (a: IUserAction, b: IUserAction) => {
  if (a.actionType !== b.actionType) {
    if (a.actionType === "score") return -1;
    else if (b.actionType === "score") return 1;
    else if (a.actionType === "compositeScore") return -1;
    else if (b.actionType === "compositeScore") return 1;
    else if (a.actionType === "removeScore") return -1;
    else if (b.actionType === "removeScore") return 1;
    return -1;
  }

  return toEpochMillis(b.loggedAt) - toEpochMillis(a.loggedAt)
}

// Drops only a second stored copy of the same action (same type and instant); an action without a key is kept.
const uniqueByActionKey = (actions: IUserAction[]) => {
  const seen = new Set<string>()
  return actions.filter((action) => {
    const key = actionKey(action)
    if (!key) return true
    if (seen.has(key)) return false
    seen.add(key)
    return true
  })
}

export const OfflineHydrator: React.FC<IOfflineHydrator> = ({ children }) => {
  const { enqueueSnackbar, closeSnackbar } = useSnackbar();
  const notifyRefused = useRefusedScoresSnackbar();
  // Mounted app-wide and outside the selection gate, so the reload prompt reaches every scoring screen.
  useClientUpdateSnackbar();

  const user = getAuthUser();
  const client = useApolloClient();
  const isQueueOpen = useQueueOpen();
  const savingScores = getIsSavingScores()
  const [isLoaded, setLoaded] = useState(false);
  const [isHydrated, setIsHydrated] = useState(false);
  const [recordUserSessionActionMutation] = useMutation(RECORD_USER_SESSION);
  // Synchronous re-entry guard: the async localStorage rehydrate below awaits
  // before isHydrated/savingScores flip, so a ref closes that double-run window.
  const hydratingRef = useRef(false);


  useEffect(() => {
    if (!isQueueOpen) {
      hydratingRef.current = false;
      setOfflineSyncSettled(false);
    }
    if (isHydrated && !isQueueOpen) {
      console.log("Went offline, prepare to sync when reconnected")
      setIsHydrated(false)
    }
  }, [isHydrated, isQueueOpen])

  useEffect(() => {
    const hydrate = async () => {
      if (hydratingRef.current) return;
      hydratingRef.current = true;
      setOfflineSyncSettled(false);
      console.log("Begin offline query hydration")
      const promises: Array<Promise<any>> = [];
      const retiredReplays: string[] = [];

      const currQueries = getTrackedQueries();

      if (!currQueries.length) {
        try {
          setTrackedQueries(JSON.parse(await store.get(QUERY_LOCAL_REF) || '').queries || []);
        } catch (e) {
          setTrackedQueries([]);
        }
      }

      // Replaying signed out sends each action without a token and drops it on the refusal;
      // left queued, they replay once a staff user signs in.
      if (user) getTrackedQueries().forEach(trackedQuery => {
        const context = JSON.parse(trackedQuery.contextJSON);
        const query = JSON.parse(trackedQuery.queryJSON);
        const variables = JSON.parse(trackedQuery.variablesJSON);

        // Removed only once the server accepted it. Removing on dispatch dropped every failed
        // replay — an offline check-in the user already saw confirmed.
        // Counted per queued query, so a replay that fails identically every pass stops retrying
        // instead of being re-sent on every reconnection for the rest of the install's life.
        const settleReplay = async (outcome: unknown) => {
          const counts = await tryRecordSyncPass({ unackedKeys: [trackedQuery.id] }, `replay ${trackedQuery.name}`);
          // Without its count the replay can't be judged for retirement, so it stays queued.
          if (!counts) return undefined;
          const attempts = counts[trackedQuery.id] || 0;
          if (decideReplayOutcome({ name: trackedQuery.name, outcome, attempts }) === 'retry') {
            console.warn('Offline mutation kept for the next sync', { name: trackedQuery.name, attempts, outcome });
            return undefined;
          }
          console.error('Offline mutation retired without saving', { name: trackedQuery.name, attempts, outcome });
          retiredReplays.push(trackedQuery.name);
          return removeTrackedQuery(trackedQuery.id);
        };

        // A replay that lands clears its count, so an unrelated later failure starts from zero.
        const settleReplaySucceeded = async () => {
          // The server has it either way; a count left behind is dropped by the end-of-sync prune.
          await tryRecordSyncPass({ unackedKeys: [], ackedKeys: [trackedQuery.id] }, `replay ${trackedQuery.name}`);
          return removeTrackedQuery(trackedQuery.id);
        };

        promises.push(
          client.mutate({
            context,
            mutation: query,
            optimisticResponse: context.optimisticResponse,
            update: UPDATE_HANDLERS[trackedQuery.name],
            variables
          })
            .then((result) => (hasGraphQLError(result) ? settleReplay(result) : settleReplaySucceeded()))
            .catch(settleReplay)
        );
      });

      if (promises.length) setIsSyncing(true);
      // Clear the banner whether the replayed mutations resolve or reject; a rejected
      // Promise.all with no catch would strand isSyncing on and hang the "Syncing" banner.
      const replaySettled = Promise.all(promises)
        .catch((e) => {
          // Each replay settles itself above; this only guards the aggregate.
          console.error('Offline mutation hydration failed...', e);
        })
        .finally(() => {
          // A retired replay never reached the server, and the user already saw it succeed
          // optimistically, so it has to be named rather than dropped quietly.
          if (retiredReplays.length) {
            enqueueSnackbar(<IntlMsg {...messages.queuedChangesNotSaved} values={{ count: retiredReplays.length }} />,
              { variant: 'error', persist: true, anchorOrigin: { vertical: 'bottom', horizontal: 'right' } });
          }
          if (!promises.length) return;
          if (timeout) clearTimeout(timeout);
          timeout = setTimeout(() => setIsSyncing(false), 3000);
        });

      console.log("Finished offline query hydration, app is now loaded")
      setLoaded(true);

      if (user) {
        console.log("Begin hydrating offline scores")
        let anyActionsToSync = false
        let overallSyncFailed = false
        let refusedCount = 0
        // Deduped across every chunk and user: one sync pass reports one notice.
        const refusedReasons = new Set<string>()
        let wedgedCount = 0
        try {
          setIsHydrated(true)
          // A scoring screen may have claimed since this effect's gate; wait for it rather than overlap.
          await waitToClaimSavingScores();

          // Loop through all sessions for curr user
          const sessions = await getCachedUserSessions()
          anyActionsToSync = Object.values(sessions).some((s) => (s || []).some(session => session.actions.length > 0))
          if (anyActionsToSync) {
            enqueueSnackbar(
              <Box display="flex" alignItems="center" justifyContent="center" style={{ gap: '10px' }}>
                <CircularProgress style={{color: 'white'}} size={20} />
                <Typography>Syncing actions...</Typography>
              </Box>,
              {variant: "default", anchorOrigin: {vertical: "bottom", horizontal: "right"}, autoHideDuration: 60000, preventDuplicate: true, key: "offline-sync"}
            )
          }
          for (const [userId, userSessions] of Object.entries(sessions)) {
            const sessionsToDelete: number[] = []
            if (userSessions?.length) {
              for (let i = 0; i < userSessions.length; i++) {
                const session = userSessions[i]
                console.log(`Submitting session ${i + 1} of ${userSessions.length} for user ${userId}`, {session})
                let allSessionActionsSucceeded = !session.actions.length  // Default to true if no actions

                // Submit all actions for each stored session
                if (session.actions.length) {
                  let syncFailed = false

                  // Batch actions in series of a hundred here, sending a second stored copy of one action (same key) once
                  const uniqueSessionActions = uniqueByActionKey(session.actions)
                  const chunks = _.chunk(uniqueSessionActions.sort(actionSort), 100)
                  console.log(`Found ${session.actions.length} of which ${uniqueSessionActions.length} are unique. Record user session actions in ${chunks.length} chunks`)

                  for (let j = 0; j < chunks.length; j++) {
                    const chunk = chunks[j]
                    // Scores compress into actions.scores, player edits ride flat under actions.playerEdits.
                    // buildUserSessionActionsEnvelope owns that split. See "recordUserSession — one `actions` envelope" in CLAUDE.md.
                    const variables = {...session, actions: buildUserSessionActionsEnvelope(chunk), newUserId: userId, skipDuplicateActions: true}
                    console.log(`Record user session action chunk ${j + 1} of ${chunks.length}`, {variables, chunk})
                    try {
                      const result = await recordUserSessionActionMutation({variables})
                      // Only actions the server acknowledged leave the device. See "Buffered actions are deleted only by their own acknowledgement" in CLAUDE.md.
                      const acked = ackedActionKeys(chunk, result?.data?.recordUserSession)
                      refusedCount += refusedScoreActionKeys(chunk, result?.data?.recordUserSession).size
                      refusedScoreReasons(chunk, result?.data?.recordUserSession).forEach((reason) => refusedReasons.add(reason))
                      if (acked.size) {
                        await removeUserSessionActions(userId, session.startedAt, acked)
                        markOfflineSyncPending()
                      }
                      // Any unacknowledged action stops this session's later chunks, so none applies ahead of it.
                      const unacked = chunk.filter((action) => !acked.has(actionKey(action) as string))
                      const unackedKeys = unacked.map((action) => actionKey(action) as string).filter(Boolean)
                      // Without counts, every unsaved action still counts as retrying this pass.
                      const counts = (await tryRecordSyncPass({ unackedKeys, ackedKeys: Array.from(acked) }, `scores for ${userId}`)) ?? {}
                      if (unacked.length) {
                        // Ordering is kept for every transient cause. Only actions that have failed
                        // identically MAX_SYNC_PASSES times stop blocking — otherwise one permanently
                        // unackable action means nothing older ever syncs again, across reloads.
                        const wedged = wedgedKeys(unackedKeys, counts)
                        if (wedged.length < unackedKeys.length) {
                          console.warn("Some actions were not saved; keeping them for the next sync", {userId, unsaved: unacked.length, chunk: j + 1})
                          syncFailed = true
                          overallSyncFailed = true
                          break;
                        }
                        console.error("Actions have not saved after repeated syncs; letting older actions through", {userId, wedged, chunk: j + 1})
                        // Console output never reaches telemetry, and this action needs a person to look at it.
                        if (ENABLE_SENTRY) Sentry.captureMessage('Buffered actions stuck after repeated syncs', { extra: { userId, wedged } } as never)
                        wedgedCount += wedged.length
                        overallSyncFailed = true
                      }
                    } catch (err) {
                      console.error("Failed to record user session", err, {i, userId})
                      syncFailed = true
                      overallSyncFailed = true
                      break;
                    }
                  }

                  // Read the store once after the loop for the authoritative post-write
                  // remaining count — summing per-chunk remainders over-counts across chunks.
                  if (!syncFailed) {
                    const latestSessions = await getCachedUserSessions()
                    const stored = (latestSessions[userId] || []).find((s) => toEpochMillis(s.startedAt) === toEpochMillis(session.startedAt))
                    if (!stored || stored.actions.length === 0) allSessionActionsSucceeded = true
                  }
                }

                if (allSessionActionsSucceeded) {
                  // Delete any empty sessions on successful handling of all actions. Any sessions with actions that failed should keep those actions in storage.
                  // If last session (ie. curr session) then delete actions only, otherwise delete session if empty
                  if (i === userSessions.length - 1 && userId === user._id)
                    userSessions[i].actions = []
                  else sessionsToDelete.push(i)
                }
              }

              await filterUserSessions(userId, userSessions.filter((_, i) => sessionsToDelete.includes(i)))
            }
          }
        } catch(e) {
          console.error("Failed on score hydration", e);
          overallSyncFailed = true
        } finally {
          console.log("Finished hydrating offline scores")
          // Report once, after every session was attempted — not per-session — so a coach who scored
          // across multiple ice times sees one spinner, then a single success/failure message.
          if (anyActionsToSync) {
            closeSnackbar("offline-sync")
            enqueueSnackbar(
              <Box display="flex" alignItems="center" justifyContent="center" style={{ gap: '10px' }}>
                <Typography>{overallSyncFailed ? 'Failed to sync actions' : 'Successfully synced offline scores!'}</Typography>
              </Box>,
              {variant: overallSyncFailed ? "error" : "success"}
            )
          }
          notifyRefused(refusedCount, Array.from(refusedReasons))
          // A wedged action is still on the device — it just stopped holding the rest hostage.
          // It needs a person, so it gets its own notice rather than the generic sync failure.
          if (wedgedCount) {
            enqueueSnackbar(<IntlMsg {...messages.scoresStuck} values={{ count: wedgedCount }} />,
              { variant: 'error', persist: true, anchorOrigin: { vertical: 'bottom', horizontal: 'right' } })
          }
          // Counts for actions that have since left the buffer would otherwise accumulate forever.
          // Bookkeeping only — a failure here must never escape the sync's own reporting.
          try {
            // Both the buffered actions and the still-queued replays count as live work; pruning
            // against only one of them would drop the other's counts on every pass.
            await pruneSyncAttempts([
              ...Object.values(await getCachedUserSessions())
                .flatMap((sessions) => (sessions || []).flatMap((s) => s.actions || []))
                .map((action) => actionKey(action) as string),
              ...getTrackedQueries().map((q) => q.id),
            ].filter(Boolean))
          } catch (err) {
            console.warn('pruneSyncAttempts failed', err)
          }
          releaseSavingScores();
          replaySettled.then(() => setOfflineSyncSettled(true));
        }
      } else {
        console.log("Skipped offline score hydration due to lack of user access")
        // Re-arm so signing in on this page load runs the replay and score sync.
        hydratingRef.current = false;
      }
    };

    // Waits out a scoring screen's submit; the flag clearing re-runs this effect.
    if (!savingScores && !isHydrated && isQueueOpen) hydrate();
  }, [isQueueOpen, user, isHydrated, savingScores]);

  if (!isLoaded) return <LoadingPage />;

  return <>{children}</>;
};
